import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import groq from 'groq';
import { BLOG_CARD, EPISODE_CARD, SPEAKERS } from 'lib/queries';
import { client } from 'lib/sanity';
import type { BlogPosts } from 'types/cms/blog/BlogPosts';

export type BlogPostsQuery = UseQueryResult & {
  data: BlogPosts;
};

export type PostsQueryKey = [string, number, string | null, string | null];

export const POSTS_KEY = 'posts';

export const INITIAL_VISIBLE_POSTS_COUNT = 5;
export const INITIAL_VISIBLE_SEARCH_POSTS_COUNT = 2;

export const fetchPosts = async ({
  queryKey,
}: QueryFunctionContext<PostsQueryKey>) => {
  const [, count, category, searchTerm] = queryKey;

  // Podcasts are a type, not a category so we need to query them differently
  const filterByPodcasts = category === 'podcasts';
  const blogCategory = filterByPodcasts ? null : category;

  const TYPE_QUERY = filterByPodcasts
    ? groq`'timeline_podcast'`
    : `'timeline_article', 'timeline_podcast'`;

  const CATEGORY_QUERY = groq`${
    blogCategory ? `&& $category in categories[]` : ''
  }`;

  const SEARCH_QUERY = groq`${
    searchTerm
      ? `&& [title, summary, pt::text(body), episode.transcript] match [$searchTerm]`
      : ''
  }`;
  const POSTS_QUERY = groq`
    *[ _type in[${TYPE_QUERY}] ${CATEGORY_QUERY} ${SEARCH_QUERY} && publishedAt < now()]
	`;

  const query = groq`
    {
      "posts": ${POSTS_QUERY} | order(publishedAt desc) {
        ${BLOG_CARD},
        ${EPISODE_CARD},
        ${SPEAKERS},
        summary
      }[0..$count],
      "totalCount": count(${POSTS_QUERY})
    }
 `;

  const response: BlogPosts = await client.fetch(query, {
    count,
    category,
    searchTerm,
  });

  return response;
};

interface UsePostsProps {
  count: number;
  category?: string | null;
  searchTerm?: string | null;
}

// -1 means all posts
// Sanity starts counting at 0
// This mean that if you want to fetch 3 posts, you have to pass 2 as count
export const usePosts = ({
  count = INITIAL_VISIBLE_POSTS_COUNT,
  category = null,
  searchTerm = null,
}: UsePostsProps) =>
  useQuery({
    queryKey: [POSTS_KEY, count, category, searchTerm],
    queryFn: fetchPosts,
    placeholderData: keepPreviousData,
  });
